import { useWorkspaceTab } from '@components/Common/EntityWorkspace/useWorkspaceTab';
import { emailWithBlockState } from '@components/BlockedAddresses/EmailBlockedChip';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import { useActivityRail } from '@components/Common/EntityWorkspace/useActivityRail';
import {
  ActivityRail,
  EntityWorkspace,
  IdentityRail,
  SectionCard,
  WorkspaceField,
} from '@components/Common/EntityWorkspace';
import RecordActivityCard from '@components/Common/EntityWorkspace/RecordActivityCard';
import type { GlanceRow, TimelineEntry, WorkspaceTab } from '@components/Common/EntityWorkspace';
import ErrorMessage from '@components/Common/ErrorMessage';
import type { EmployersFormData } from '@components/Employers/types';
import PasswordDialog from '@components/Employers/PasswordDialog';
import SendContractDialog from '@components/Employers/SendContractDialog';
import { useToast } from '@components/Common/Toast/ToastProvider';
import RecordPickerDialog from '@components/Common/RecordPickerDialog';
import type { PickerRow } from '@components/Common/RecordPickerDialog';
import {
  cancelEmployerSubscription,
  companyApplicationCounts,
  companyJobsForAssignment,
  employerJobsForAssignment,
  employersByCompany,
  setEmployerJobsOnCompany,
  employerOnboardingSave,
  getEmployerById,
  getPipelineHealthQuery,
  rejectPremiumRequestMutation,
  setEmployerPremiumMutation,
  setEmployerApproval,
  updateEmployer,
} from '@constants/graphQLQueries';
import { fetchGraphQLApiData } from '@helpers/graphQLApi';
import ConfirmDialog from '@components/Common/ConfirmDialog';
import { canAssignJobs, canWriteJobsAndCompanies, isSuperAdmin, useCurrentUserRole } from '@components/Common/Utils/roles';
import { toHrUsers } from '@components/Common/Utils/useHrUsers';
import WorkspaceInput from '@components/Common/WorkspaceInput';
import axios from 'axios';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryIcon from '@mui/icons-material/History';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutlineOutlined';
import { Box, Button, Checkbox, Chip, FormControlLabel, Switch, Typography } from '@mui/material';
import AgreementsPanel from './AgreementsPanel';
import { pendingContractStep, reviewPending } from './decisions';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ViewActivityButton from '@components/Common/EntityWorkspace/ViewActivityButton';
import { applicantLabel, companyJobsPath } from '@components/Common/Utils/filterLinks';
import { formatDate, formatDateTime } from '@helpers/date';
import { humanizeEnum } from '@helpers/labels';

const TABS: WorkspaceTab[] = [
  { id: 'account', label: 'Account', icon: <PersonOutlineIcon /> },
  { id: 'jobs', label: 'Jobs', icon: <WorkOutlineIcon /> },
  { id: 'subscription', label: 'Subscription', icon: <CreditCardIcon /> },
  // Beside Subscription, because both answer the same question about a client: what have we
  // agreed with them. One is what they are paying, the other is what they signed.
  { id: 'agreements', label: 'Agreements', icon: <DescriptionOutlinedIcon /> },
  { id: 'history', label: 'History', icon: <HistoryIcon /> },
];

const initialsOf = (name?: string) =>
  (name || '').split(' ').filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('') || 'E';
const fmt = (d?: string) => formatDate(d);
const displayName = (pd: any) =>
  (pd?.fullName || [pd?.firstName, pd?.lastName].filter(Boolean).join(' ')).trim();

const mapUserToForm = (user: any): EmployersFormData => ({
  id: user._id || '',
  fullName: displayName(user.personalDetails),
  firstName: user.personalDetails?.firstName || '',
  lastName: user.personalDetails?.lastName || '',
  email: user.personalDetails?.email || '',
  phone: user.personalDetails?.phone || '',
  locality: user.personalDetails?.locality || '',
  placeId: user.personalDetails?.placeId || '',
  lat: user.personalDetails?.lat ?? null,
  lng: user.personalDetails?.lng ?? null,
  city: user.personalDetails?.city || '',
  state: user.personalDetails?.state || '',
  zipcode: user.personalDetails?.zipCode || '',
  country: user.personalDetails?.country || '',
  companyId: user.companyId || null,
  companyName: user.companyName || '',
  jobsPosted: user.jobsPosted || 0,
  jobsLeft: user.jobsLeft || 0,
  planType: user.planType || '',
  jobValidityDays: user.jobValidityDays || 0,
  links: {
    instagram: user.links?.instagram || '',
    linkedin: user.links?.linkedin || '',
    website: user.links?.website || '',
    behance: user.links?.behance || '',
    googleDrive: user.links?.googleDrive || '',
    facebook: user.links?.facebook || '',
    twitter: user.links?.twitter || '',
  },
  createdAt: user.createdAt || '',
  createdBy: user.createdBy || '',
  updatedAt: user.updatedAt || '',
  updatedBy: user.updatedBy || '',
});

const ONBOARDING_STEPS = [
  'Contract signed',
  'Kickoff call',
  'Filters captured',
  'First demand posted',
  'First submission',
];

/**
 * Labels for the self-declaration collected at registration.
 *
 * Duplicated from mintly-ui's src/lib/employerBusiness.ts rather than shared, because these two
 * apps have no common package. The stored values are stable codes, so this map can only go stale
 * by gaining a new option, and an unmapped code falls through to itself rather than rendering
 * blank.
 */
/*
 * Every value the schema defines, in its order. WHOLESALER was missing, and the lookup falls back
 * to the raw stored code -- so the one production employer who declared it had "WHOLESALER"
 * rendered at them where every other account shows a label. A silent gap: the fallback means a
 * missing entry never errors, it just looks like a database leak on screen.
 */
const BUSINESS_MODEL_LABELS: Record<string, string> = {
  RETAIL: 'Retail',
  ONLINE: 'Online',
  OMNICHANNEL: 'Omnichannel',
  DISTRIBUTOR: 'Distributor',
  WHOLESALER: 'Wholesaler',
  MANUFACTURER: 'Manufacturer',
  STAFFING_AGENCY: 'Staffing/Recruitment Agency',
  OTHER: 'Other',
};

const HIRING_FOR_LABELS: Record<string, string> = {
  OWN_COMPANY: 'My own company',
  OTHER_CLIENTS: 'Other companies or clients',
};

/** The two answers Mintly does not serve. Either one on its own is disqualifying. */
const isAgencyDeclaration = (businessModel?: string | null, hiringFor?: string | null) =>
  businessModel === 'STAFFING_AGENCY' || hiringFor === 'OTHER_CLIENTS';

const EmployerWorkspace: React.FC = () => {
  const { employerId } = useParams<{ employerId: string }>();
  const navigate = useNavigate();
  const [form, setForm] = useState<EmployersFormData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /* In the URL, not in state: a tab is a place, and this one was unshareable and did
     not survive a reload. See useWorkspaceTab. */
  const [activeTab, setActiveTab] = useWorkspaceTab(TABS, 'account');
  const [editing, setEditing] = useState<string | null>(null);
  const [dirty, setDirty] = useState(false);

  /**
   * Managed Hiring is held apart from `form` on purpose. It is toggled by its
   * own mutation with a server-side invariant (removing it also ends the
   * dedicated-HR assignment), and the generic employer update input does not
   * carry these fields at all — folding them into the save payload would send
   * them on every unrelated section save.
   */
  const [managed, setManaged] = useState<{
    isPremium: boolean;
    premiumTier: string | null;
    assignedHrId: string | null;
    premiumRequestStatus: string | null;
  } | null>(null);
  const [managedSaving, setManagedSaving] = useState(false);
  /**
   * Account review, held outside `form` for the same reason as `managed`: it is written by its
   * own Super-Admin-only mutation and is deliberately absent from EmployerUpdateInput, so an
   * employer cannot approve themselves by saving their profile.
   */
  const [approval, setApproval] = useState<{
    status: string | null;
    reason: string | null;
    decidedAt: string | null;
    decidedBy: string | null;
  } | null>(null);
  const [approvalSaving, setApprovalSaving] = useState(false);
  /**
   * What the employer said about themselves at registration. Self-declared, unverified, and read
   * only. It informs the review; it is not part of it, which is why it sits beside `approval`
   * rather than inside it.
   */
  const [declaration, setDeclaration] = useState<{
    businessModel: string | null;
    hiringFor: string | null;
  } | null>(null);
  /*
   * How this employer signs in. Read-only and server-owned, so it sits beside `declaration` rather
   * than inside `form`: EmployersFormData's field names are the update mutation's, and
   * `authProvider` is never written from this screen. (It could not be anyway —
   * EmployerUpdateHelper drops it.)
   *
   * `null` means absent, which is not the same as unknown-and-dangerous: the field postdates the
   * earliest accounts, and those sign in with a password. Only an explicit GOOGLE hides the
   * password control.
   */
  const [authProvider, setAuthProvider] = useState<string | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);
  /* The deciding admin's name. `approvalDecidedBy` stores an id, and an audit line reading
     "by 6a524701a741956de50fde08" tells the next Super Admin nothing about who to go and ask. */
  const [decidedByName, setDecidedByName] = useState<string | null>(null);
  const [declineOpen, setDeclineOpen] = useState(false);
  /*
   * The last time a Super Admin cancelled this employer's plan, and why. Server-owned, like
   * `approval`: the cancel mutation writes it and EmployerUpdateInput never does.
   */
  const [planCancel, setPlanCancel] = useState<{
    at: string | null;
    by: string | null;
    reason: string | null;
  } | null>(null);
  const [cancelledByName, setCancelledByName] = useState<string | null>(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelSaving, setCancelSaving] = useState(false);
  // Asked every time and unticked by default: closing someone's live jobs is the larger act, and
  // whether it is wanted depends on why the plan is being cancelled.
  const [closeJobsOnCancel, setCloseJobsOnCancel] = useState(false);
  const [managedOffOpen, setManagedOffOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [sendContractOpen, setSendContractOpen] = useState(false);
  const [demand, setDemand] = useState<{
    openJobs?: number;
    filledJobs?: number;
    jobsWithZeroApplicants?: number;
    rolesOpenOver30Days?: number;
    stuckCandidates?: number;
  } | null>(null);
  const [onboardingDone, setOnboardingDone] = useState<Record<string, boolean>>({});
  // Per-job HR assignment (managed employers)
  const [assignJobs, setAssignJobs] = useState<
    Array<{ _id: string; title?: string; jobType?: string; hiringMode?: string; jobStatus?: string; assignedHrId?: string | null }>
  >([]);
  const [hrUsers, setHrUsers] = useState<Array<{ _id: string; name: string }>>([]);
  // Which of the company's jobs this employer holds, and the picker that changes it.
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [savingJobs, setSavingJobs] = useState(false);
  const [heldJobIds, setHeldJobIds] = useState<string[]>([]);
  const { showToast } = useToast();
  const role = useCurrentUserRole();
  // Marketing reads employer accounts but changes none (owner's brief, 2026-10-19).
  const canWriteEmployer = canWriteJobsAndCompanies(role);
  const snapshot = useRef<EmployersFormData | null>(null);
  const [activityOpen, toggleActivity] = useActivityRail();


  const load = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await fetchGraphQLApiData(getEmployerById(id));
      if (!data?.getEmployerById) {
        setError('Employer not found.');
        return;
      }
      const record = data.getEmployerById;
      setForm(mapUserToForm(record));
      setManaged({
        isPremium: !!record.isPremium,
        premiumTier: record.premiumTier ?? null,
        assignedHrId: record.assignedHrId ?? null,
        premiumRequestStatus: record.premiumRequestStatus ?? null,
      });
      setApproval({
        status: record.approvalStatus ?? null,
        reason: record.approvalReason ?? null,
        decidedAt: record.approvalDecidedAt ?? null,
        decidedBy: record.approvalDecidedBy ?? null,
      });
      setDeclaration({
        businessModel: record.businessModel ?? null,
        hiringFor: record.hiringFor ?? null,
      });
      setAuthProvider(record.authProvider ?? null);
      setPlanCancel({
        at: record.planCancelledAt ?? null,
        by: record.planCancelledBy ?? null,
        reason: record.planCancelReason ?? null,
      });
      // Same best-effort name lookup as the approver's: an id tells the next admin nothing.
      setCancelledByName(null);
      if (record.planCancelledBy) {
        try {
          const cu = await axios.get(`/api/users/${record.planCancelledBy}`);
          const p = cu?.data?.personalDetails ?? {};
          setCancelledByName(
            p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || null,
          );
        } catch {
          /* the date and the reason still say what happened */
        }
      }
      // Resolve the approver to a name. Best-effort, like the roll-ups below: if the lookup
      // fails the line falls back to the date alone rather than printing the raw id, which is
      // an audit value the database keeps and a human cannot use.
      setDecidedByName(null);
      if (record.approvalDecidedBy) {
        try {
          const au = await axios.get(`/api/users/${record.approvalDecidedBy}`);
          const p = au?.data?.personalDetails ?? {};
          setDecidedByName(
            p.fullName || [p.firstName, p.lastName].filter(Boolean).join(' ') || null,
          );
        } catch {
          /* the date still tells them when */
        }
      }
      const steps: Array<{ key?: string; value?: string }> = record.onboardingSteps ?? [];
      const doneMap: Record<string, boolean> = {};
      steps.forEach((s) => {
        if (s?.key) doneMap[s.key] = !!(s.value && String(s.value).trim());
      });
      setOnboardingDone(doneMap);
      // Open-vs-filled demand roll-up for a managed client (Excel PrimarySecondary).
      if (record.isPremium) {
        try {
          const dh = await fetchGraphQLApiData(getPipelineHealthQuery({ employerId: id }));
          setDemand(dh?.data?.getPipelineHealth ?? null);
        } catch {
          /* demand summary is best-effort */
        }
        try {
          const jr = await fetchGraphQLApiData(employerJobsForAssignment(id));
          setAssignJobs(jr?.data?.getAllJobs?.content ?? []);
        } catch {
          /* job list is best-effort */
        }
        try {
          const ur = await axios.get('/api/users?page=0&size=200');
          setHrUsers(toHrUsers(ur?.data?.content ?? ur?.data ?? []));
        } catch {
          /* HR list is best-effort */
        }
      } else {
        setDemand(null);
        setAssignJobs([]);
      }
    } catch {
      setError('Failed to load the employer. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (employerId) load(employerId);
  }, [employerId, load]);

  const toggleOnboarding = async (step: string, checked: boolean) => {
    const next = { ...onboardingDone, [step]: checked };
    setOnboardingDone(next);
    try {
      const arr = ONBOARDING_STEPS.filter((s) => next[s]).map((s) => ({ key: s, value: 'done' }));
      await fetchGraphQLApiData(employerOnboardingSave(employerId || '', arr), {
        showStatus: true,
        message: 'Onboarding updated',
      });
    } catch {
      setError('Failed to update onboarding. Please try again.');
    }
  };

  // Per-job HR assignment is edited on the central /hr hub; this page shows a read-only
  // summary (via the loaded assignJobs + hrUsers) with a link there.
  const hrNameById = useMemo(() => {
    const map: Record<string, string> = {};
    hrUsers.forEach((u) => (map[u._id] = u.name));
    return map;
  }, [hrUsers]);

  const setField = (key: keyof EmployersFormData, value: unknown) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    setDirty(true);
  };

  const startEdit = (id: string) => {
    if (editing && editing !== id && dirty) return;
    snapshot.current = form ? { ...form } : null;
    setEditing(id);
    setDirty(false);
  };

  const cancelEdit = () => {
    if (snapshot.current) setForm(snapshot.current);
    setEditing(null);
    setDirty(false);
  };

  const saveSection = async () => {
    if (!form) return;
    setLoading(true);
    try {
      await fetchGraphQLApiData(updateEmployer(form), {
        showStatus: true,
        message: 'Employer updated',
      });
      await load(form.id);
      setEditing(null);
      setDirty(false);
    } catch {
      setError('Failed to save changes. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const timeline: TimelineEntry[] = useMemo(() => {
    if (!form) return [];
    const t: TimelineEntry[] = [];
    if (form.updatedAt)
      t.push({ id: 'u', kind: 'status', text: 'Record updated', meta: `${fmt(form.updatedAt)} · ${form.updatedBy || 'System'}` });
    if (form.createdAt)
      t.push({ id: 'c', kind: 'submitted', text: 'Account created', meta: `${fmt(form.createdAt)} · ${form.createdBy || 'System'}` });
    return t;
  }, [form]);

  /**
   * Toggling Managed Hiring is its own mutation, not part of the section save.
   *
   * Turning it OFF also deactivates the dedicated-HR assignment server-side, which removes that
   * HR's access to this client's applications, so that direction is confirmed first. The
   * confirmation is `ConfirmDialog`, not `window.confirm`: a native dialog cannot be styled,
   * blocks the whole tab, and is the thing the shared component exists to replace.
   *
   * Split in two so the dialog has something to call on confirm. `handleManagedToggle` decides
   * whether to ask; `applyManagedToggle` performs the mutation either way.
   */
  const applyManagedToggle = async (next: boolean) => {
    setManagedSaving(true);
    try {
      await fetchGraphQLApiData(setEmployerPremiumMutation(employerId || '', next), {
        showStatus: true,
        message: next ? 'Marking as Premium Hiring client' : 'Removing Premium Hiring',
      });
      await load(employerId || '');
    } catch {
      showToast('Failed to update Premium Hiring status', 'error');
    } finally {
      setManagedSaving(false);
    }
  };

  const handleManagedToggle = (next: boolean) => {
    if (!next && managed?.assignedHrId) {
      setManagedOffOpen(true);
      return;
    }
    void applyManagedToggle(next);
  };

  /** Resolve a request without granting it, which unblocks the self-serve flow. */
  const handleKeepRegular = async () => {
    setManagedSaving(true);
    try {
      await fetchGraphQLApiData(rejectPremiumRequestMutation(employerId || ''), {
        showStatus: true,
        message: 'Keeping as a regular client',
      });
      await load(employerId || '');
    } catch {
      showToast('Failed to update the request', 'error');
    } finally {
      setManagedSaving(false);
    }
  };

  /**
   * Record the account review decision.
   *
   * Lives on the record rather than the Employers list, where it used to be: deciding from a
   * name and an email in a table row is a rubber stamp. Here the reviewer has the contact
   * details, the company, the plan and the activity in front of them first.
   */
  /**
   * Absent means approved, matching `EmployerApprovalStatus.isBlocking` on the backend: every
   * employer created before the field carries null, and showing them as "unknown" would invite
   * someone to fix the whole customer base in bulk.
   */
  const approvalState = (approval?.status ?? '').toUpperCase() || 'APPROVED';
  /* Only an explicit GOOGLE hides the password control. Null is an account older than the field,
     and those sign in with a password. */
  const isGoogleAccount = (authProvider ?? '').toUpperCase() === 'GOOGLE';
  // setEmployerApproval is Super-Admin-only; the BFF blocks HR, so offering the button would
  // only ever produce a 403.
  const canReview = isSuperAdmin(role);

  const decideApproval = async (
    status: 'APPROVED' | 'DECLINED',
    reason?: string,
  ) => {
    setApprovalSaving(true);
    try {
      await fetchGraphQLApiData(
        setEmployerApproval(employerId || '', status, reason),
        {
          showStatus: true,
          message: status === 'APPROVED' ? 'Approving account' : 'Declining account',
        },
      );
      setDeclineOpen(false);
      await load(employerId || '');
    } catch {
      showToast('Could not save that decision. Nothing was changed.', 'error');
    } finally {
      setApprovalSaving(false);
    }
  };

  /*
   * A plan to act on: a plan type, or credits still in hand. Drives both the cancel button and
   * whether the last cancellation note is still current (see the subscription card).
   */
  const hasActivePlan = !!form?.planType || (form?.jobsLeft ?? 0) > 0;

  /*
   * Cancel the plan. Super Admin only, like the approval above, and only offered while there is
   * something to cancel: the backend refuses an employer with no plan and no credits, so the
   * button would only ever produce that refusal.
   */
  const canCancelSubscription = isSuperAdmin(role) && hasActivePlan;

  const openCancelSubscription = () => {
    setCloseJobsOnCancel(false);
    setCancelOpen(true);
  };

  const cancelSubscription = async (reason?: string) => {
    setCancelSaving(true);
    try {
      await fetchGraphQLApiData(
        cancelEmployerSubscription(employerId || '', reason ?? '', closeJobsOnCancel),
      );
      setCancelOpen(false);
      showToast(
        closeJobsOnCancel ? 'Subscription cancelled and live jobs closed' : 'Subscription cancelled',
        'success',
      );
      await load(employerId || '');
    } catch (e: any) {
      const detail = String(e?.message ?? '').replace(/^GraphQL errors:\s*/, '');
      showToast(detail || 'Could not cancel the subscription. Nothing was changed.', 'error');
    } finally {
      setCancelSaving(false);
    }
  };

  /**
   * Open the delete confirmation.
   *
   * The guard stays here rather than in the dialog: a record with no name and no email has
   * nothing to type back, and ConfirmDialog treats an empty `confirmText` as "fall back to
   * typing DELETE", which would quietly turn the strictest action in the workspace into a
   * one-click delete. Better to refuse to open.
   */
  const openDeleteEmployer = () => {
    if (!(form?.fullName || form?.email || '').trim()) {
      showToast('This record has no name or email to confirm against.', 'error');
      return;
    }
    setDeleteOpen(true);
  };

  /**
   * The delete itself. Type-to-confirm now lives in ConfirmDialog, which compares trimmed and
   * case-insensitively and keeps the button disabled until it matches, so by the time this runs
   * the admin has already typed the name back. It replaces a `window.prompt`, which could not be
   * styled, and whose returned string this had to re-validate by hand.
   */
  const handleDeleteEmployer = async () => {
    try {
      await fetchGraphQLApiData(
        `mutation { deleteEmployerById(_id: "${employerId}") { _id } }`,
        { showStatus: true, message: 'Deleting employer' },
      );
      showToast('Employer deleted', 'success');
      navigate('/employers');
    } catch {
      showToast('Failed to delete the employer', 'error');
    }
  };

  /*
   * Above the `if (!form) return` guards below, and it must stay there.
   *
   * This sat after them, so on the first render -- while the employer is still loading and the
   * component returns a skeleton -- the hook did not run, and it did once the data arrived.
   * React counts hooks per render: "Rendered more hooks than during the previous render",
   * minified error #310, and the whole tab failed to load.
   *
   * `form` is therefore nullable here, which is why companyId is read with `?.`.
   */
  /**
   * Rows for the job picker: every job on this employer's company, whoever holds it.
   *
   * Company-scoped because setEmployerJobsOnCompany is. That is not a limitation in practice --
   * of 27 distinct job owners in production, 26 resolve to an employer and every one of those is
   * attached to a company. An employer with no companyId holds no jobs, which is why the panel
   * says so rather than offering a control that cannot work.
   */
  const searchCompanyJobs = useCallback(
    async (query: string): Promise<PickerRow[]> => {
      const companyId = form?.companyId;
      if (!companyId) return [];
      const [res, holdersRes, appsRes] = await Promise.all([
        fetchGraphQLApiData(companyJobsForAssignment(companyId)).catch(() => null),
        fetchGraphQLApiData(employersByCompany(companyId)).catch(() => null),
        fetchGraphQLApiData(companyApplicationCounts(companyId)).catch(() => null),
      ]);
      const jobs = res?.data?.getAllJobs?.content ?? [];

      /*
       * Applicants per job. Ticking a box moves the job AND its applicants, so this is the fact
       * an admin most needs before doing it and the row did not carry it.
       *
       * Counted from one fetch of the company's applications rather than a query per row. If the
       * company has more than the page holds the tally would silently under-report, so that case
       * shows nothing instead of a wrong number: a missing count reads as unknown, a low one
       * reads as safe to move.
       */
      const apps = appsRes?.data?.getAllJobApplications;
      const complete =
        apps != null && (apps.totalElements ?? 0) <= (apps.content?.length ?? 0);
      const appCount: Record<string, number> = {};
      if (complete) {
        for (const a of apps.content ?? []) {
          if (a?.jobId) appCount[a.jobId] = (appCount[a.jobId] ?? 0) + 1;
        }
      }

      /*
       * Owner ids resolved to emails, so a row names the account holding the job rather than an
       * id an admin cannot evaluate before taking it from them.
       *
       * A local map, deliberately, not state. It was `setOwnerEmails(byId)` with `ownerEmails` in
       * this callback's dependency list: a fresh object each run gave the callback a new identity,
       * RecordPickerDialog re-ran the effect that calls it, and the dialog sat on "Searching…"
       * forever. Its own comment asks for a stable onSearch. The holders are fetched in this same
       * call, so nothing needed to be stored to begin with.
       */
      const byId: Record<string, string> = {};
      (holdersRes?.data?.getAllEmployers?.content ?? []).forEach((e: any) => {
        if (e?._id) byId[e._id] = e?.personalDetails?.email ?? e._id;
      });

      // Recomputed on each search rather than kept in state: the picker reopens after a save, and
      // stale ticks would misdescribe ownership -- which matters more here than in an add-only
      // picker, because an unticked row RELEASES a job.
      setHeldJobIds(jobs.filter((j: any) => j.employerId === employerId).map((j: any) => j._id));

      const q = query.trim().toLowerCase();
      return jobs
        .filter((j: any) => !q || (j.title ?? '').toLowerCase().includes(q))
        .map((j: any) => {
          const owner = j.employerId && j.employerId !== 'null' ? j.employerId : '';
          const ownerLabel = !owner
            ? 'unassigned'
            : owner === employerId
              ? 'held by this employer'
              : `owned by ${byId[owner] ?? 'another account'}`;
          return {
            id: j._id,
            title: j.title ?? j._id,
            subtitle: [
              j.jobStatus,
              j.postedDate ? fmt(j.postedDate) : null,
              complete ? applicantLabel(appCount[j._id] ?? 0) : null,
            ]
              .filter(Boolean)
              .join(' · '),
            meta: ownerLabel,
          };
        });
    },
    [form?.companyId, employerId],
  );

  const handleSaveJobs = async (ids: string[]) => {
    if (!form?.companyId || !employerId) return;
    setSavingJobs(true);
    try {
      await fetchGraphQLApiData(
        setEmployerJobsOnCompany(form.companyId as string, employerId, ids),
      );
      setJobPickerOpen(false);
      showToast('Jobs updated for this employer.', 'success');
      if (employerId) load(employerId);
    } catch {
      showToast('Could not update this employer\'s jobs.', 'error');
    } finally {
      setSavingJobs(false);
    }
  };

  if (loading && !form) return <SkeletonLoader variant="workspace" />;
  if (error && !form) return <ErrorMessage page message={error} />;
  if (!form) return null;

  const isEditing = (id: string) => editing === id;
  const input = (key: keyof EmployersFormData) => (
    <WorkspaceInput value={form[key] as string} onChange={(v) => setField(key, v)} />
  );

  const glance: GlanceRow[] = [
    { key: 'Company', value: form.companyName || '-' },
    { key: 'Jobs left', value: String(form.jobsLeft ?? 0), mono: true },
    { key: 'Plan', value: form.planType || '-' },
  ];

  /*
   * A pending contract is the record's primary action while there is one: it is the step a client
   * who asked for Premium Hiring is waiting on, and it otherwise sits in the fourth card of the
   * Account tab. Super Admin only because sendPremiumContract and setEmployerPremium are refused to
   * an HR by the BFF; the same buttons stay in the Premium Hiring card below.
   */
  const contractStep = isSuperAdmin(role) ? pendingContractStep(managed) : null;
  const contractAction =
    contractStep === 'send' ? (
      <Button variant="contained" disabled={managedSaving} onClick={() => setSendContractOpen(true)}>
        Send contract
      </Button>
    ) : contractStep === 'activate' ? (
      <Button variant="contained" disabled={managedSaving} onClick={() => handleManagedToggle(true)}>
        Activate Premium Hiring
      </Button>
    ) : undefined;

  /*
   * The review decision, above the cards on every tab while the account is waiting for it. The
   * Account review card keeps the history (who decided, when, and why); this is where the decision
   * is taken. Decline goes through the reason dialog below, which the backend requires.
   */
  const reviewWaiting = canReview && reviewPending(approval?.status);
  const decisionBar = reviewWaiting ? (
    <SectionCard title="Account review" icon={<VerifiedOutlinedIcon />} meta="Decision needed">
      <Box sx={{ gridColumn: '1 / -1' }}>
        <Typography variant="body2" sx={{ color: 'var(--tx-mid)', mb: 1.25 }}>
          This employer is waiting for review. Until the account is approved they can reach only
          their profile and settings: no subscription, no company, no job posts.
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="success"
            size="small"
            disabled={approvalSaving}
            onClick={() => decideApproval('APPROVED')}
          >
            Approve account
          </Button>
          <Button
            variant="outlined"
            color="error"
            size="small"
            disabled={approvalSaving}
            onClick={() => setDeclineOpen(true)}
          >
            Decline with a reason
          </Button>
        </Box>
      </Box>
    </SectionCard>
  ) : undefined;

  const identityRail = (
    <IdentityRail
      initials={initialsOf(form.fullName)}
      name={form.fullName || 'Employer'}
      subId={form.id}
      primaryAction={contractAction}
      secondaryActions={<ViewActivityButton actorId={form.id} />}
      glance={glance}
      audit={{
        createdAt: form.createdAt,
        createdBy: form.createdBy,
        updatedAt: form.updatedAt,
        updatedBy: form.updatedBy,
      }}
    />
  );

  const account = (
    <>
      <SectionCard
        title="Account contact"
        icon={<PersonOutlineIcon />}
        meta="6 fields"
        editing={isEditing('contact')}
        onEdit={canWriteEmployer ? () => startEdit('contact') : undefined}
        onCancel={cancelEdit}
        onSave={saveSection}
      >
        <WorkspaceField label="First name" value={form.firstName} editing={isEditing('contact')} edit={input('firstName')} />
        <WorkspaceField label="Last name" value={form.lastName} editing={isEditing('contact')} edit={input('lastName')} />
        <WorkspaceField label="Email" value={emailWithBlockState(form.email)} mono editing={isEditing('contact')} edit={input('email')} />
        <WorkspaceField label="Phone" value={form.phone} mono editing={isEditing('contact')} edit={input('phone')} />
        {/*
          * Read-only, because there is nowhere to write it. companyName is not a field on the
          * employer -- it is joined from the company record -- and EmployerUpdateInput has no such
          * field, so updateEmployer never sent it. It was offered as an editable box that accepted
          * a change, reported success, and reverted on reload. The company's name is edited on the
          * company, which is the record that owns it.
          */}
        <WorkspaceField label="Company" value={form.companyName} />
        <WorkspaceField label="City" value={form.city} editing={isEditing('contact')} edit={input('city')} />

        {/* What the employer said about their own business at signup.

            This sat inside the Account review card, which is wrapped in `canReview` and therefore
            renders only for a Super Admin. That was the wrong place: the declaration is a FACT
            about the employer, like their email or their company, not part of the authority to
            approve or decline them. An HR user looking at the record could not see it at all,
            which defeats the point of collecting it.

            Read-only in both cases. approvalStatus remains the decision and still moves only
            through setEmployerApproval. */}
        <WorkspaceField
          label="Business model"
          value={
            declaration?.businessModel
              ? (BUSINESS_MODEL_LABELS[declaration.businessModel] ??
                declaration.businessModel)
              : 'Not asked at signup'
          }
        />
        <WorkspaceField
          label="Hiring for"
          value={
            declaration?.hiringFor
              ? (HIRING_FOR_LABELS[declaration.hiringFor] ??
                declaration.hiringFor)
              : 'Not asked at signup'
          }
        />
        {isAgencyDeclaration(
          declaration?.businessModel,
          declaration?.hiringFor,
        ) && (
          <Chip
            size="small"
            color="error"
            label="Declared as an agency"
            sx={{ mt: 0.5 }}
          />
        )}
      </SectionCard>

      {/* How this employer signs in, and the one action attached to it.

          No `onEdit`: the section is read-only, and the password is not a field you type into but
          a credential the server mints. Same shape as Premium Hiring below. */}
      <SectionCard title="Login &amp; security" icon={<LockOutlinedIcon />}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            mb: 1,
          }}
        >
          <Chip
            size="small"
            label={isGoogleAccount ? 'Google' : 'Email & password'}
            sx={
              isGoogleAccount
                ? { bgcolor: 'var(--indigo-tint)', color: 'var(--indigo)' }
                : { bgcolor: 'var(--acc-tint)', color: 'var(--acc)' }
            }
          />
          {/* Hidden rather than disabled for a Google account: there is nothing to act on, and a
              greyed-out button invites someone to keep clicking it. */}
          {!isGoogleAccount && (
            <Button
              size="small"
              variant="outlined"
              onClick={() => setPasswordOpen(true)}
            >
              Update password
            </Button>
          )}
        </Box>
        <Typography variant="body2" sx={{ color: 'var(--tx-mid)' }}>
          {isGoogleAccount
            ? 'This employer signs in with Google. There is no Mintly password on file to set or share.'
            : 'This employer signs in with an email and password issued by Mintly. Use Update password to generate fresh credentials to share with them directly, for example when handing a Premium Hiring account over to a client so they can review their own applicants.'}
        </Typography>
      </SectionCard>

      {/* Account review sits above Managed Hiring because it gates everything below it: until
          an account is approved it cannot subscribe, add a company or post at all. Absent means
          approved, matching the backend, because every employer created before the field carries
          null. */}
      {canReview && (
        <SectionCard title="Account review" icon={<VerifiedOutlinedIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <Chip
              size="small"
              label={
                approvalState === 'PENDING'
                  ? 'Under review'
                  : approvalState === 'DECLINED'
                    ? 'Declined'
                    : 'Approved'
              }
              color={
                approvalState === 'PENDING'
                  ? 'warning'
                  : approvalState === 'DECLINED'
                    ? 'error'
                    : 'success'
              }
              variant={approvalState === 'APPROVED' ? 'outlined' : 'filled'}
            />
            {approval?.decidedAt && (
              <Typography variant="caption" sx={{ color: 'var(--tx-mid)' }}>
                {`Decided ${formatDate(approval.decidedAt)}`}
                {decidedByName ? ` by ${decidedByName}` : ''}
              </Typography>
            )}
          </Box>

          {approvalState === 'DECLINED' && approval?.reason && (
            <Typography variant="body2" sx={{ color: 'var(--tx-mid)', mb: 1.25 }}>
              {`Reason given: ${approval.reason}`}
            </Typography>
          )}


          <Typography variant="body2" sx={{ color: 'var(--tx-mid)', mb: 1.25 }}>
            {approvalState === 'APPROVED'
              ? 'This employer can subscribe, add a company and post jobs.'
              : 'Until this account is approved the employer can reach only their profile and settings.'}
          </Typography>

          {/* alignSelf matters here, and its absence is why "Approve account" rendered at
              roughly double height. `.cc-section__body` is a CSS grid with no `align-items`, so a
              grid item stretches to the tallest cell in its row; this Box being `display:flex`
              then passed that stretch down to the Button. The row height is set by the two-line
              sentence beside it, so a `size="small"` button was drawn two lines tall. Nothing to
              do with the variant, the colour or the theme. Same idiom as the "Manage in HR"
              button further down, which already guards against this. */}
          {/* While pending, the decision bar above the tabs carries these two. */}
          {!reviewWaiting && (
          <Box
            sx={{
              display: 'flex',
              alignSelf: 'flex-start',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            {approvalState !== 'APPROVED' && (
              /* `outlined`, not `contained`. theme.ts specs outlinedSuccess/Error/Primary and
                 says nothing about contained-plus-a-semantic-colour, so `contained success`
                 fell through to stock MUI: success.main is an on-dark lift green, whose
                 automatic contrast text lands on near-black. It rendered as a green slab with
                 dark text beside an outlined Decline. The outlined pair is the console's
                 convention (10 of 12 semantic buttons) and is the variant the theme draws. */
              <Button
                variant="outlined"
                color="success"
                size="small"
                disabled={approvalSaving}
                onClick={() => decideApproval('APPROVED')}
              >
                Approve account
              </Button>
            )}
            {approvalState !== 'DECLINED' && (
              <Button
                variant="outlined"
                color="error"
                size="small"
                disabled={approvalSaving}
                onClick={() => setDeclineOpen(true)}
              >
                Decline
              </Button>
            )}
          </Box>
          )}
        </SectionCard>
      )}

      {/* Managed Hiring. This lives on the employer record because that is
          where the decision is made and the client is unambiguous; assigning
          the dedicated HR afterwards happens on the HR page. */}
      <SectionCard title="Premium Hiring" icon={<VerifiedOutlinedIcon />}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!managed?.isPremium}
                disabled={managedSaving}
                onChange={(e) => handleManagedToggle(e.target.checked)}
              />
            }
            label={managed?.isPremium ? 'Premium Hiring client' : 'Self-serve'}
          />
          <Typography variant="body2" sx={{ color: 'var(--tx-mid)', mt: 0.5 }}>
            {managed?.isPremium ? (
              <>
                Tier <strong>{humanizeEnum(managed?.premiumTier || 'MANAGED')}</strong>
                {managed?.assignedHrId
                  ? ' · dedicated HR assigned. Manage the assignment on the HR page.'
                  : ' · assign a dedicated HR on the HR page.'}
              </>
            ) : (
              'Turn this on to make them a Premium Hiring client. They then appear on the HR page, where a dedicated HR can be assigned.'
            )}
          </Typography>

          {/* Request review, shown only while they have asked and are not yet managed. */}
          {!managed?.isPremium
            && (managed?.premiumRequestStatus === 'PENDING'
              || managed?.premiumRequestStatus === 'CONTRACT_SENT') && (
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                borderRadius: '6px',
                border: '1px solid var(--amber-line)',
                bgcolor: 'var(--amber-tint)',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'var(--tx-hi)' }}>
                {managed?.premiumRequestStatus === 'CONTRACT_SENT'
                  ? 'Premium Hiring request: agreement sent'
                  : 'Premium Hiring requested'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'var(--tx-mid)', mb: 1.25 }}>
                {managed?.premiumRequestStatus === 'CONTRACT_SENT'
                  ? 'The agreement has been emailed. Once it is signed (offline), Activate to turn Premium Hiring on, then assign a dedicated HR on the HR page.'
                  : 'This employer asked for Premium Hiring. Send them the agreement, or keep them as a regular self-serve client.'}
              </Typography>
              {/* Same stretch as the Account review row above: without alignSelf these grow
                  to the height of the paragraph beside them. */}
              <Box
                sx={{
                  display: 'flex',
                  alignSelf: 'flex-start',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  disabled={managedSaving}
                  onClick={() => setSendContractOpen(true)}
                >
                  {managed?.premiumRequestStatus === 'CONTRACT_SENT'
                    ? 'Resend agreement'
                    : 'Send contract'}
                </Button>
                {managed?.premiumRequestStatus === 'CONTRACT_SENT' && (
                  <Button
                    variant="contained"
                    color="success"
                    size="small"
                    disabled={managedSaving}
                    onClick={() => handleManagedToggle(true)}
                  >
                    Activate Premium Hiring
                  </Button>
                )}
                <Button
                  variant="outlined"
                  size="small"
                  disabled={managedSaving}
                  onClick={handleKeepRegular}
                >
                  Keep Regular
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </SectionCard>

      {managed?.isPremium && (
        <SectionCard title="Demands" icon={<VerifiedOutlinedIcon />} meta="Open vs filled roles">
          <WorkspaceField label="Open roles" value={String(demand?.openJobs ?? 0)} mono />
          <WorkspaceField label="Filled roles" value={String(demand?.filledJobs ?? 0)} mono />
          <WorkspaceField
            label="Roles with no applicants"
            value={String(demand?.jobsWithZeroApplicants ?? 0)}
            mono
          />
          <WorkspaceField
            label="Open over 30 days"
            value={String(demand?.rolesOpenOver30Days ?? 0)}
            mono
          />
        </SectionCard>
      )}

      {managed?.isPremium && (
        <SectionCard title="Onboarding" icon={<VerifiedOutlinedIcon />} meta="Premium Hiring client setup">
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column' }}>
            {ONBOARDING_STEPS.map((step) => (
              <FormControlLabel
                key={step}
                control={
                  <Checkbox
                    checked={!!onboardingDone[step]}
                    onChange={(e) => toggleOnboarding(step, e.target.checked)}
                  />
                }
                label={step}
              />
            ))}
          </Box>
        </SectionCard>
      )}

      {managed?.isPremium && (
        <SectionCard
          title="Job assignments"
          icon={<VerifiedOutlinedIcon />}
          meta="One HR per job — assigned on the HR page"
        >
          <Box sx={{ gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', gap: 1 }}>
            {assignJobs.filter((j) => j.hiringMode === 'MANAGED').length === 0 ? (
              <div style={{ fontSize: 'var(--font-size-2xs)', color: 'var(--tx-mid)' }}>
                No Premium Hiring jobs yet. Post jobs for this client, then assign each to an HR.
              </div>
            ) : (
              assignJobs
                .filter((j) => j.hiringMode === 'MANAGED')
                .map((j) => {
                  const hrName = j.assignedHrId ? hrNameById[j.assignedHrId] : '';
                  return (
                    <Box
                      key={j._id}
                      sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'space-between' }}
                    >
                      <div
                        style={{
                          flex: 1,
                          minWidth: 0,
                          fontSize: 'var(--font-size-sm)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {j.title || 'Untitled'}
                      </div>
                      <div
                        style={{
                          fontSize: 'var(--font-size-2xs)',
                          flexShrink: 0,
                          color: hrName ? 'var(--tx-hi)' : 'var(--tx-mid)',
                        }}
                      >
                        {hrName || 'Unassigned'}
                      </div>
                    </Box>
                  );
                })
            )}
            {/* Only where /hr opens: a Super Admin or an HR Lead. An HR would be bounced off it. */}
            {canAssignJobs(role) && (
              <Button
                size="small"
                variant="text"
                onClick={() => navigate('/hr')}
                sx={{ alignSelf: 'flex-start' }}
              >
                Manage in HR
              </Button>
            )}
          </Box>
        </SectionCard>
      )}

      <SectionCard title="Plan & usage" icon={<CreditCardIcon />} meta="4 fields">
        <WorkspaceField label="Plan type" value={form.planType || '-'} />
        <WorkspaceField label="Jobs posted" value={String(form.jobsPosted ?? 0)} mono />
        <WorkspaceField label="Jobs left" value={String(form.jobsLeft ?? 0)} mono />
        <WorkspaceField label="Job validity" value={form.jobValidityDays ? `${form.jobValidityDays} days` : '-'} mono />
      </SectionCard>

      {canWriteEmployer && (
      <SectionCard title="Danger zone" icon={<DeleteOutlineIcon />}>
        <Box sx={{ gridColumn: '1 / -1' }}>
          <Button variant="outlined" color="error" size="small" onClick={openDeleteEmployer}>
            Delete employer
          </Button>
          <Typography variant="body2" sx={{ color: 'var(--tx-low)', mt: 1 }}>
            Permanent, and their jobs and applications are left orphaned. Removing Premium Hiring
            above is usually what is wanted instead.
          </Typography>
        </Box>
      </SectionCard>
      )}

      <SectionCard title="Links" icon={<LinkOutlinedIcon />}>
        <WorkspaceField label="Website" full value={form.links?.website || '-'} mono />
        <WorkspaceField label="LinkedIn" full value={form.links?.linkedin || '-'} mono />
        <WorkspaceField label="Instagram" full value={form.links?.instagram || '-'} mono />
      </SectionCard>
    </>
  );

  const tabContent: Record<string, React.ReactNode> = {
    account,
    // Mounted only when its tab is open: the panel loads a list and this workspace already makes
    // several requests on mount without adding one nobody has asked to see.
    agreements: employerId ? <AgreementsPanel employerId={employerId} /> : null,
    jobs: (
      <SectionCard title="Jobs" icon={<WorkOutlineIcon />} meta={`${heldJobIds.length} held`}>
        {form.companyId ? (
          <>
            <WorkspaceField
              label="Jobs"
              full
              value={
                /*
                 * `filter_companyId` was singly encoded and named a column absent from the Jobs
                 * page's map, so graphQLFilterFormatter sent `{ key: "undefined" }`. companyJobsPath
                 * builds it the way SearchFilter writes one, against a column that now exists.
                 */
                <Link to={companyJobsPath(form.companyId)} className="cc-link">
                  View this company&apos;s jobs
                </Link>
              }
            />
            {/*
              Was a link keyed on company NAME. Two companies sharing one, or a rename, and it
              listed the wrong jobs -- or none, silently.
            */}
            <WorkspaceField
              label="Assignment"
              full
              value={
                /* Super Admin only: setEmployerJobsOnCompany moves jobs and their applicants
                   between employers, and the BFF refuses it to everyone else. */
                isSuperAdmin(role) ? (
                  <Button variant="outlined" size="small" onClick={() => setJobPickerOpen(true)}>
                    Choose jobs…
                  </Button>
                ) : (
                  <span className="cc-field__value">A Super Admin assigns this employer&apos;s jobs.</span>
                )
              }
            />
          </>
        ) : (
          <WorkspaceField
            label="Jobs"
            full
            /*
              Not attached to a company, so there is nothing to scope a job list to -- and no jobs
              to show either: every employer in production that holds a job has a companyId.
              Saying so beats a disabled control that looks like a fault.
            */
            value="This employer is not attached to a company, so they hold no jobs. Approve a company request for them first."
          />
        )}
      </SectionCard>
    ),
    subscription: (
      <SectionCard title="Subscription" icon={<CreditCardIcon />} meta="3 fields">
        {/* "No plan", not "Pay Per Job": after a cancel planType is null, and naming a plan they
            no longer hold would read as the cancel not having happened. */}
        <WorkspaceField label="Plan" value={humanizeEnum(form.planType, 'No plan')} />
        <WorkspaceField label="Jobs remaining" value={String(form.jobsLeft ?? 0)} mono />
        <WorkspaceField label="Validity" value={form.jobValidityDays ? `${form.jobValidityDays} days` : '-'} mono />
        {/* Only while the plan is actually cancelled. After a rebuy planType is set again, and the
            old cancellation line sitting above a live plan and its Cancel button read as a
            contradiction ("Cancelled on …" over an active Pay per job). */}
        {!hasActivePlan && planCancel?.at && (
          <Typography
            variant="body2"
            sx={{ gridColumn: '1 / -1', color: 'var(--tx-mid)' }}
          >
            {`Cancelled on ${formatDateTime(planCancel.at)}`}
            {cancelledByName ? ` by ${cancelledByName}` : ''}
            {planCancel.reason ? `: ${planCancel.reason}` : ''}
          </Typography>
        )}
        {canCancelSubscription && (
          /* alignSelf for the same reason as the approval buttons: the section body is a grid
             with no align-items, so a bare button stretches to the tallest cell in its row. */
          <Box sx={{ gridColumn: '1 / -1', alignSelf: 'flex-start' }}>
            <Button
              variant="outlined"
              color="error"
              size="small"
              disabled={cancelSaving}
              onClick={openCancelSubscription}
            >
              Cancel subscription
            </Button>
          </Box>
        )}
      </SectionCard>
    ),
    history: (
      <>
        <SectionCard title="Record activity" icon={<HistoryIcon />}>
          <WorkspaceField label="Created" value={`${fmt(form.createdAt)} · ${form.createdBy || 'System'}`} full />
          <WorkspaceField label="Last updated" value={`${fmt(form.updatedAt)} · ${form.updatedBy || 'System'}`} full />
        </SectionCard>
        <RecordActivityCard recordId={form.id} />
      </>
    ),
  };

  return (
    <>
    <EntityWorkspace
      backLabel="Employers"
      onBack={() => navigate('/employers')}
      identityRail={identityRail}
      tabs={TABS}
      activeTabId={activeTab}
      onTabChange={setActiveTab}
      stepper={decisionBar}
      activityRail={<ActivityRail timeline={timeline} />}
      activityOpen={activityOpen}
      onToggleActivity={toggleActivity}
      isDirty={dirty && !!editing}
      dirtySectionLabel="Account contact"
      onDiscard={cancelEdit}
      onSave={saveSection}
    >
      {tabContent[activeTab]}

      {/*
        Same picker as the company request page, in its multi-select mode. Confirm is "Save"
        because it sets the end state: ticked jobs become this employer's, and any of theirs left
        unticked go back to unowned. That is the "remove a job now, add one later" case, and it is
        why the tab exists rather than sending an admin to a one-time request screen.
      */}
      <RecordPickerDialog
        open={jobPickerOpen}
        multiple
        title={`Jobs for ${form.email || 'this employer'}`}
        searchPlaceholder="Search jobs on this company…"
        confirmLabel="Save"
        onSearch={searchCompanyJobs}
        onConfirmMany={handleSaveJobs}
        onClose={() => setJobPickerOpen(false)}
        submitting={savingJobs}
        initialSelectedIds={heldJobIds}
      />
    </EntityWorkspace>

    {/* The reason is required: the backend rejects a decline without one, and the employer is
        shown this text on their own screen and in the decline email, so it has to say what they
        can act on. */}
    <ConfirmDialog
      open={declineOpen}
      title="Decline this account?"
      body="They keep access to their profile and settings and can still reach support. They cannot subscribe, add a company or post until this is reversed."
      reasonLabel="Reason"
      reasonPlaceholder="What can they fix, or why is this not a fit?"
      actionLabel="Decline"
      busy={approvalSaving}
      onCancel={() => setDeclineOpen(false)}
      onConfirm={(reason) => decideApproval('DECLINED', reason)}
    />

    {/* The employer starts from scratch, so the reason is required: it is what the next admin
        reads on this tab when the client asks why their plan is gone. */}
    <ConfirmDialog
      open={cancelOpen}
      title="Cancel this subscription?"
      body="They lose their plan and every remaining job credit, and will have to buy again to post. Payments are not refunded here; record a refund on the Subscriptions page."
      reasonLabel="Reason"
      reasonPlaceholder="Why is this plan being cancelled?"
      actionLabel="Cancel subscription"
      busy={cancelSaving}
      onCancel={() => setCancelOpen(false)}
      onConfirm={(reason) => void cancelSubscription(reason)}
    >
      <FormControlLabel
        control={
          <Checkbox
            checked={closeJobsOnCancel}
            onChange={(e) => setCloseJobsOnCancel(e.target.checked)}
          />
        }
        label="Also close their live jobs"
      />
    </ConfirmDialog>

    {/* Turning Managed Hiring off is confirmed because it also deactivates the dedicated-HR
        assignment server-side, which removes that HR's access to this client's applications.
        A switch that silently revokes someone else's access is not a switch. */}
    <ConfirmDialog
      open={managedOffOpen}
      title="Remove Premium Hiring from this client?"
      body="Their dedicated HR assignment will be deactivated, which also removes that HR's access to this client's job applications."
      actionLabel="Remove"
      busy={managedSaving}
      onCancel={() => setManagedOffOpen(false)}
      onConfirm={() => {
        setManagedOffOpen(false);
        void applyManagedToggle(false);
      }}
    />

    {/* Type-to-confirm, because the record cannot be brought back. `confirmText` is the name
        or email shown on screen; ConfirmDialog compares it trimmed and case-insensitively and
        keeps the button genuinely disabled until it matches. */}
    <ConfirmDialog
      open={deleteOpen}
      title="Delete this employer permanently?"
      body="Their jobs and applications are not deleted and will be orphaned."
      confirmText={form?.fullName || form?.email || ''}
      confirmLabel={form?.fullName ? 'Name' : 'Email'}
      actionLabel="Delete"
      onCancel={() => setDeleteOpen(false)}
      onConfirm={() => {
        setDeleteOpen(false);
        void handleDeleteEmployer();
      }}
    />

    <PasswordDialog
      open={passwordOpen}
      employerId={employerId || ''}
      employerEmail={form?.email || ''}
      onClose={() => setPasswordOpen(false)}
    />

    <SendContractDialog
      open={sendContractOpen}
      employerId={employerId || ''}
      onClose={() => setSendContractOpen(false)}
      onSent={() => {
        setSendContractOpen(false);
        load(employerId || '');
      }}
    />
    </>
  );
};

export default EmployerWorkspace;
